import { useCallback, useMemo } from 'react';

import { __ } from '@eventespresso/i18n';
import { Clickable } from '@eventespresso/adapters';
import { InlineEditCurrency } from '@eventespresso/ui-components';
import { CurrencyDisplay } from '@eventespresso/ee-components';
import { useMemoStringify } from '@eventespresso/hooks';
import { useMoneyDisplay } from '@eventespresso/services';
import { isLocked } from '@eventespresso/predicates';
import { useLockedTicketAction } from '@eventespresso/tpc';
import useRecalculateBasePrice from '../../hooks/useRecalculateBasePrice';

import type { InlineEditCurrencyProps } from '@eventespresso/ui-components';
import type { TicketItemProps } from '../types';

import './style.scss';

interface EditablePriceProps extends TicketItemProps {
	className?: string;
}

const EditablePrice: React.FC<Partial<EditablePriceProps>> = ({ entity: ticket, className }) => {
	const { afterAmount, beforeAmount, localizeMoney, parseLocalizedMoney, currency } = useMoneyDisplay();

	const recalculateBasePrice = useRecalculateBasePrice(ticket.id);

	const onChangePrice = useCallback<InlineEditCurrencyProps['onChange']>(
		(result) => {
			if (result && 'amount' in result) {
				const price = Math.abs(parseFloat(result.amount.toString()));
				if (price !== ticket.price) {
					recalculateBasePrice(price);
				}
			}
		},
		[recalculateBasePrice, ticket.price]
	);

	const { alertContainer, showAlert } = useLockedTicketAction(ticket, 'COPY/TRASH');

	const wrapperProps = useMemoStringify({ className });

	const isTicketLocked = isLocked(ticket);

	const tooltip = __('edit ticket total…');

	const pattern = useMemo<RegExp>(() => {
		const { decimalMark: maybeDm, thousandsSeparator: maybeTs } = currency;
		const ts = /\s/.test(maybeTs) ? '\\s' : maybeTs;
		/* eslint-disable-next-line no-useless-escape */
		const dm = maybeDm === '.' ? '\.' : maybeDm;
		// ^[0-9,]*\.?[0-9]{0,3} where ',' is ts, '.' is dm, and 3d.p.
		return new RegExp(`^[0-9${ts}]*${dm}?[0-9]{0,6}$`);
	}, [currency]);

	return isTicketLocked ? (
		<Clickable as='div' className='ee-ticket-editable-price' onClick={showAlert}>
			<CurrencyDisplay className={className} value={ticket.price} />
			{alertContainer}
		</Clickable>
	) : (
		<InlineEditCurrency
			afterAmount={afterAmount}
			amount={ticket.price}
			beforeAmount={beforeAmount}
			pattern={pattern}
			parseAmount={parseLocalizedMoney}
			formatAmount={localizeMoney}
			id={ticket.id}
			placeholder={__('set price…')}
			wrapperProps={wrapperProps}
			onChange={onChangePrice}
			tag={'h3'}
			tooltip={tooltip}
		/>
	);
};

export default EditablePrice;
